"use client";

import { TagVariant } from "@/lib/types";
import { cn, getUniqueTags } from "@/lib/utils";
import { useEffect, useState } from "react";
import { getSliderTitle } from "@/components/strapi/nsc/Slider";
import Tag from "@/components/ui/tag";
import RichText from "@/components/general/nsc/RichText";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSearchParams } from "next/navigation";
import { Plus } from "lucide-react";

const AccordionArchiveSectionOneColumn = ({ items, variant, noTags, data }: any) => {
  let uniqueTags: TagVariant[] = [];

  if (!noTags) {
    items = items.map((item: any) => {
      return {
        ...item,
        attributes: {
          ...item.attributes,
          tags: item.attributes.tags.map((tag: any) => {
            return tag.name;
          }),
        },
      };
    });

    uniqueTags = getUniqueTags(items);
    if (variant !== "Over ons") {
      uniqueTags = getUniqueTags(items);
    } else {
      uniqueTags = ["Over ons"];
    }
  }

  const [openItems, setOpenItems] = useState([items[0]?.id]);
  const [selectedItem, setSelectedItem] = useState(items[0]);

  const searchParams = useSearchParams();
  const item = searchParams.get("item");

  const scrollToItem = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleItem = (itemToToggle: any) => {
    setOpenItems(prev => {
      if (prev.includes(itemToToggle.id)) {
        return prev.filter(id => id !== itemToToggle.id);
      } else {
        return [...prev, itemToToggle.id];
      }
    });
    setSelectedItem(itemToToggle);
  };

  useEffect(() => {
    if (item && items.find((i: any) => item === i.title.toLowerCase().replaceAll(" ", "-"))) {
      scrollToItem(item);
      const foundItem = items.find((i: any) => item === i.title.toLowerCase().replaceAll(" ", "-"));
      setSelectedItem(foundItem);
      setOpenItems(prev => {
        if (!prev.includes(foundItem.id)) {
          return [...prev, foundItem.id];
        }
        return prev;
      });
    }
  }, [item]); // TODO: Fix this warning

  return (
    <section className="mb-16 md:mb-24">
      {/* Desktop */}
      <div className="hidden grid-cols-12 gap-x-5 md:grid">
        {variant !== "Speerpunt" && (
          <div className="col-span-12 mb-6 flex flex-wrap items-center gap-4 md:mb-12 md:gap-6">
            <h2 className="header-3">{data?.title || getSliderTitle(variant)}</h2>
            <div className="h-px w-full flex-1 bg-nsc-dark-blue opacity-40" />
            {!noTags && (
              <div className="flex gap-2">
                {uniqueTags.map((tag, index) => (
                  <Tag key={index} variant={tag} rounded />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="col-span-4">
          {items.map((item: any) => (
            <div key={item.id} className="border-b border-[rgba(23,28,96,0.4)] py-4">
              <div
                className={`group flex cursor-pointer flex-col gap-5 py-2 transition-all md:col-span-6 md:flex-row md:items-center md:gap-5 [&[data-custom-state=open]>svg]:!-rotate-90`}
                onClick={() => toggleItem(item)}
                data-custom-state={`${openItems.includes(item.id) ? "open" : "closed"}`}
              >
                <p className="flex max-w-[400px] flex-1 items-start gap-2 text-start font-roboto font-bold text-nsc-dark-blue">
                  <span>{item?.attributes?.title || item.title}</span>
                </p>

                <Plus className="ml-auto w-5 -rotate-90 transform transition-transform" />
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-8 col-start-6">
          {(selectedItem?.attributes?.shortDescription || selectedItem?.attributes?.description || selectedItem?.description) && (
            <>
              <h2 className="mb-8 text-balance font-oswald text-[32px] font-bold uppercase leading-[110%] lg:text-[48px]">
                {selectedItem?.attributes?.title || selectedItem?.title}
              </h2>
              <RichText
                data={
                  selectedItem?.attributes?.shortDescription || selectedItem?.attributes?.description || selectedItem?.description
                }
                className="rich-text"
              />
            </>
          )}
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        {variant !== "Speerpunt" && (
          <div className="col-span-12 mb-6 flex flex-wrap items-center gap-4 md:mb-12 md:gap-6">
            <h2 className="header-3">{data?.title || getSliderTitle(variant)}</h2>
            <div className="h-px w-full flex-1 bg-nsc-dark-blue opacity-40" />
            <div className="flex gap-2">
              {uniqueTags.map((tag) => (
                <Tag key={tag} variant={tag} rounded />
              ))}
            </div>
          </div>
        )}

        <Accordion type="multiple" className="w-full">
          {items.map((item: any) => (
            <AccordionItem key={item.id} value={item.id}>
              <AccordionTrigger id={`accordion-${item.id}`} onClick={() => setSelectedItem(item)}>
                {item?.attributes?.title || item.title}
              </AccordionTrigger>
              <AccordionContent>
                {(item?.attributes?.description || item?.description || item?.attributes?.shortDescription) && (
                  <RichText
                    data={
                      item?.attributes?.shortDescription ||
                      item?.attributes?.description ||
                      item?.description
                    }
                    className="rich-text"
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default AccordionArchiveSectionOneColumn;
